import classNames from 'classnames'
import {useCallback, useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate, useParams} from 'react-router-dom'
import Breadcrumb from '../../components/core/breadcrumb'
import {SimpleBlueButton} from '../../components/core/button'
import ContentLoader from '../../components/core/loader'
import Modal from '../../components/core/modal'
import {PageTitle, ResponsivePageTitle} from '../../components/core/title'
import Footer from '../../components/layouts/footer'
import LogsBarChart from '../../components/pages/logManagement/LogsBarChart'
import LogsList from '../../components/pages/logManagement/LogsList'
import conditionTypes from '../../data/conditionTypes.json'
import {routes} from '../../routes'
import {getLogs} from '../../stores/log-management'
import {setActiveTimeIndex} from '../../stores/monitoring'
import {setSidebarIsActive} from '../../stores/sidebar'
import DateHelper from '../../utils/date'
import renderSkeletonLoaders from '../../utils/loader'
import FilterFields from './FilterFields'
import PageHeader from './PageHeader'
import text from './text.json'

const LOGS_LIMIT = 100
const initialFieldPair = {
    field_name: '',
    condition: conditionTypes.log_management_conditions.values.equal,
    value: ''
}
const DEFAULT_FIELDS = {
    message: {
        field_name: 'message',
        filter_name: 'message_like'
    },
    log_group_id: 'log_group_id'
}

const LogsDashboard = () => {
    const {id: logGroupId} = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const isFetching = useSelector(state => state.log_management.isFetching)

    const [logs, setLogs] = useState({count: '', fields: [], data: []})
    const [offset, setOffset] = useState(0)
    const [isFirstLoad, setIsFirstLoad] = useState(true)
    const [fieldPairs, setFieldPairs] = useState([initialFieldPair])
    const [draftFields, setDraftFields] = useState([initialFieldPair])
    const [filtersQuery, setFiltersQuery] = useState('')
    const [dateRangeQuery, setDateRangeQuery] = useState('')
    const [isFiltersModalActive, setIsFiltersModalActive] = useState(false)
    const [isLoadMoreEnabled, setIsLoadMoreEnabled] = useState(true)
    const [isLoadMoreButtonLoading, setIsLoadMoreButtonLoading] = useState(false)
    const [searched, setSearched] = useState('')

    const logsContainerRef = useRef(null)

    const filteredFieldsCount = fieldPairs.filter(f => f.field_name && f.value).length
    const logsFields = logs?.fields?.length > 0 ? logs?.fields : Object.keys(DEFAULT_FIELDS)

    const buildQueryString = useCallback((base, filters, dateRange, offset = null) => {
        let query = `${base}?${DEFAULT_FIELDS.log_group_id}=${logGroupId}`
        if (filters) query += `&${filters}`
        if (dateRange) query += `&${dateRange}`
        if (offset !== null) query += `&offset=${offset}`
        return query
    }, [logGroupId])

    const fetchLogsData = useCallback(async({
        filters = '',
        dateRange = '',
        reset = true,
        isFirstLoad = false
    } = {}) => {
        if (!logGroupId) return

        const query = buildQueryString('', filters, dateRange)

        if (reset) {
            setOffset(0)
            setIsLoadMoreEnabled(true)
        }

        const result = await dispatch(getLogs(query, navigate, true))
        if (!result) return

        setLogs(prev => ({
            count: result.count,
            data: reset ? result.data : [...prev.data, ...result.data],
            fields: isFirstLoad ? result.fields : prev.fields
        }))

        setFiltersQuery(filters)
        setDateRangeQuery(dateRange)
        setIsFirstLoad(false)

        if ((result.count || 0) < LOGS_LIMIT) {
            setIsLoadMoreEnabled(false)
        }
    }, [dispatch, logGroupId, navigate, buildQueryString])

    const loadMoreLogs = async() => {
        setIsLoadMoreButtonLoading(true)
        const newOffset = offset + 1
        const query = buildQueryString('', filtersQuery, dateRangeQuery, newOffset)
        const result = await dispatch(getLogs(query, navigate))
        if (!result) return

        setLogs(prev => ({
            ...prev,
            data: [...prev.data, ...(result.data || [])]
        }))

        setOffset(newOffset)

        if ((result?.count || 0) < LOGS_LIMIT) {
            setIsLoadMoreEnabled(false)
        }

        setIsLoadMoreButtonLoading(false)
    }

    const clearFilters = () => {
        const reset = [initialFieldPair]
        setFieldPairs(reset)
        setDraftFields(reset)
        setFiltersQuery('')
        if (filteredFieldsCount > 0) {
            fetchLogsData({filters: '', dateRange: dateRangeQuery})
        }
    }

    const applyFilters = () => {
        const validFields = draftFields.filter(f => f.field_name && f.value)
        const filters = validFields.map(f => `${f.field_name === DEFAULT_FIELDS.message.field_name ?
            DEFAULT_FIELDS.message.filter_name : f.field_name}=${f.value}`).join('&')

        setFieldPairs(draftFields)
        setFiltersQuery(filters)
        if (validFields.length > 0) {
            setSearched('')
            fetchLogsData({filters, dateRange: dateRangeQuery})
        }
        setIsFiltersModalActive(false)
    }

    const handleSearch = () => {
        const searchKey = DEFAULT_FIELDS.message.filter_name
        const hasMessageLike = filtersQuery.includes(`${searchKey}=`)
        const trimmedSearch = searched?.trim()

        if (!trimmedSearch && hasMessageLike) {
            const params = new URLSearchParams(filtersQuery)
            params.delete(searchKey)
            const updatedFilters = params.toString()
            fetchLogsData({filters: updatedFilters, dateRange: dateRangeQuery, reset: true})
            return
        }

        if (!trimmedSearch) return

        const params = new URLSearchParams(filtersQuery)
        params.set(searchKey, trimmedSearch)
        const updatedFilters = params.toString()

        const filteredFieldPairs = fieldPairs.filter(field => field.field_name !== DEFAULT_FIELDS.message.field_name)
        setFieldPairs(filteredFieldPairs)

        fetchLogsData({filters: updatedFilters, dateRange: dateRangeQuery})
    }

    const handleDateRangeChange = ({startDate, endDate}) => {
        const from = DateHelper.convertToTimeZone(startDate)
        const to = DateHelper.convertToTimeZone(endDate)
        const dateQuery = `from_time=${from}&to_time=${to}`

        setDateRangeQuery(dateQuery)
        fetchLogsData({filters: filtersQuery, dateRange: dateQuery})
        setS
    }

    const handleModalClose = () => {
        setIsFiltersModalActive(false)
        setDraftFields([...fieldPairs])
    }

    useEffect(() => {
        dispatch(setSidebarIsActive(false))
    }, [])

    useEffect(() => {
        if (logGroupId) {
            fetchLogsData({isFirstLoad: true})
            dispatch(setActiveTimeIndex(null))
        }
    }, [logGroupId])

    useEffect(() => {
        if (isFiltersModalActive) {
            setDraftFields([...fieldPairs])
        }
    }, [isFiltersModalActive])

    return (
        <main className={classNames('main', {
            dark: isDarkMode
        })} id="logs-dashboard">
            {isFiltersModalActive && (
                <Modal
                    isOpen
                    id="large-modal"
                    title={text.filters}
                    closeModal={handleModalClose}
                    contentClassName="overflow-auto"
                >
                    <FilterFields
                        logsFields={logsFields}
                        isDarkMode={isDarkMode}
                        fieldPairs={draftFields}
                        setFieldsPairs={setDraftFields}
                    />
                    <div className="apply-filter">
                        <SimpleBlueButton onClick={clearFilters} className="cancel-button">
                            {text.clear}
                        </SimpleBlueButton>
                        <SimpleBlueButton onClick={applyFilters}>
                            {text.apply}
                        </SimpleBlueButton>
                    </div>
                </Modal>
            )}
            <div>
                <ResponsivePageTitle />
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <Breadcrumb data={[routes.log_groups_list, text.section]} />
                            <PageTitle>
                                <h1 className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{text.title}</h1>
                            </PageTitle>
                        </div>
                    </div>
                </div>
                <PageHeader
                    isDarkMode={isDarkMode}
                    setIsFiltersModalActive={setIsFiltersModalActive}
                    filteredFieldsCount={filteredFieldsCount}
                    onClearFilters={clearFilters}
                    setSearched={setSearched}
                    onSearch={handleSearch}
                    searched={searched}
                    onResetDate={() => {
                        fetchLogsData({filters: filtersQuery})
                        setDateRangeQuery('')
                        dispatch(setActiveTimeIndex(null))
                    }}
                    callbackFn={handleDateRangeChange}
                />
                <div
                    ref={logsContainerRef}
                    className={classNames('logs-chart', {
                        dark: isDarkMode,
                        'chart-loader': isFetching || isFirstLoad,
                        'display-none': !isFetching && logs.data.length === 0
                    })}
                >
                    {(isFetching || isFirstLoad)
                        ? <ContentLoader />
                        : <LogsBarChart
                            logs={logs.data}
                            logsContainerRef={logsContainerRef}
                            isDarkMode={isDarkMode}
                        />
                    }
                </div>
                {(isFetching || isFirstLoad)
                    ? <div className={classNames('loader-container', {
                        dark: isDarkMode
                    })}>
                        {renderSkeletonLoaders(10)}
                    </div>
                    : logs.data.length > 0 && (
                        <LogsList
                            logsData={logs.data}
                            onLoadMore={loadMoreLogs}
                            isDarkMode={isDarkMode}
                            isLoadMoreButtonLoading={isLoadMoreButtonLoading}
                            isLoadMoreEnabled={isLoadMoreEnabled}
                        />
                    )}
            </div>
            <Footer />
        </main>
    )
}

export default LogsDashboard
